"use client";
import { AnimatePresence, Transition, motion } from "motion/react";
import {
  Children,
  cloneElement,
  ReactElement,
  useEffect,
  useState,
  useId
} from "react";
import { cn } from "../utils/cn.js";

type TabsSegmentedProps<T extends string> = {
  children: ReactElement<{ "data-id": T }>[] | ReactElement<{ "data-id": T }>;
  defaultValue?: T;
  onValueChange?: (newActiveId: T | null) => void;
  className?: string;
  transition?: Transition;
  enableHover?: boolean;
};

export function TabsSegmented<T extends string>({
  children,
  defaultValue,
  onValueChange,
  className,
  transition,
  enableHover = false
}: TabsSegmentedProps<T>) {
  const [activeId, setActiveId] = useState<T | null>(null);
  const uniqueId = useId();

  const handleSetActiveId = (id: T | null) => {
    setActiveId(id);

    if (onValueChange) {
      onValueChange(id);
    }
  };

  useEffect(() => {
    if (defaultValue !== undefined) {
      setActiveId(defaultValue);
    }
  }, [defaultValue]);

  return Children.map(children, (child: any, index) => {
    const id = child.props["data-id"];

    const interactionProps = enableHover
      ? {
          onMouseEnter: () => handleSetActiveId(id),
          onMouseLeave: () => handleSetActiveId(null)
        }
      : {
          onClick: () => handleSetActiveId(id)
        };

    return cloneElement(
      child,
      {
        key: index,
        className: cn("relative inline-flex", child.props.className),
        "aria-selected": activeId === id,
        "data-checked": activeId === id ? "true" : "false",
        ...interactionProps
      },
      <>
        <AnimatePresence initial={false}>
          {activeId === id && (
            <motion.div
              layoutId={`background-${uniqueId}`}
              className={cn("absolute inset-0", className)}
              transition={transition}
              initial={{ opacity: defaultValue ? 1 : 0 }}
              animate={{
                opacity: 1
              }}
              exit={{
                opacity: 0
              }}
            />
          )}
        </AnimatePresence>
        <span className="z-10">{child.props.children}</span>
      </>
    );
  });
}
